/* Independent page enhancements. Shopify handles catalog, variants, forms and checkout. */
(() => {
  if (customElements.get('bc-pages')) return;
  class BCPages extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.setAttribute('data-enhanced', '');
      this.setupVideos(signal);
      this.addEventListener('click', event => {
        const mediaButton = event.target.closest('[data-show-media]');
        if (mediaButton) {
          const gallery = mediaButton.closest('[data-bcp-gallery]');
          gallery.querySelectorAll('[data-media-id]').forEach(item => {
            const selected = item.dataset.mediaId === mediaButton.dataset.showMedia;
            item.hidden = !selected;
            item.classList.toggle('is-active', selected);
            if (!selected) {
              item.querySelectorAll('video').forEach(video => video.pause());
              item.querySelectorAll('iframe').forEach(frame => { frame.src = frame.src; });
            }
          });
          gallery.querySelectorAll('[data-show-media]').forEach(button => button.setAttribute('aria-pressed', String(button === mediaButton)));
        }
        const quantityButton = event.target.closest('[data-quantity-step]');
        if (quantityButton) {
          const input = quantityButton.closest('.bcp-quantity-controls').querySelector('input');
          const min = Number(input.min) || 1;
          const max = input.max ? Number(input.max) : Infinity;
          const step = Number(input.step) || 1;
          const value = Number(input.value) || min;
          input.value = Math.min(max, Math.max(min, value + Number(quantityButton.dataset.quantityStep) * step));
          input.dispatchEvent(new Event('change', { bubbles:true }));
        }
      }, { signal });
      this.querySelectorAll('[data-bcp-sort] select').forEach(select => {
        select.addEventListener('change', () => {
          const url = new URL(location.href);
          url.searchParams.set('sort_by', select.value);
          url.searchParams.delete('page');
          location.assign(url.toString());
        }, { signal });
      });
      this.querySelectorAll('[data-bcp-faq]').forEach(faq => {
        const input = faq.querySelector('[data-faq-search]');
        faq.querySelector('.bcp-faq-search').hidden = false;
        input.addEventListener('input', () => {
          const query = input.value.trim().toLocaleLowerCase();
          let found = 0;
          faq.querySelectorAll('[data-faq-item]').forEach(item => {
            item.hidden = !item.textContent.toLocaleLowerCase().includes(query);
            if (!item.hidden) found++;
          });
          faq.querySelector('[data-faq-empty]').hidden = found > 0;
          faq.querySelector('[data-faq-status]').textContent = `${found} ${found === 1 ? 'question' : 'questions'} found`;
        }, { signal });
      });
      if (this.hasAttribute('data-external-motion')) {
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.querySelectorAll('[data-bcp-videos] video').forEach(video => video.pause());
        }, { signal });
        return;
      }
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.enabled = !this.reduced.matches;
      this.sprinkles = this.dataset.sprinkles === 'true';
      this.hasMotion = this.sprinkles || !!this.querySelector('.bcp-ribbon--moving');
      this.active = false;
      this.travel = 0;
      this.lastTime = null;
      this.frame = 0;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'bcp-sprinkle-canvas';
      this.canvas.id = `bcp-motion-${this.dataset.sectionId}`;
      this.canvas.setAttribute('aria-hidden', 'true');
      this.ctx = this.canvas.getContext('2d');
      this.toggle = document.createElement('button');
      this.toggle.type = 'button';
      this.toggle.className = 'bcp-motion';
      this.toggle.hidden = true;
      this.toggle.setAttribute('aria-label', 'Toggle decorative motion');
      this.toggle.setAttribute('aria-pressed', String(this.enabled));
      this.toggle.setAttribute('aria-controls', this.canvas.id);
      document.body.append(this.canvas, this.toggle);
      const colors = ['pink','aqua','purple','yellow'].map(color => getComputedStyle(this).getPropertyValue(`--bcp-${color}`).trim());
      this.particles = Array.from({length:22}, (_,i) => ({ x:(i*.6180339887+.17)%1, y:(i*.4142135623+.09)%1, size:6+i%5, angle:i*1.47, speed:.35+(i%7)*.075, color:colors[i%4] }));
      this.toggle.addEventListener('click', () => { this.enabled=!this.enabled; this.updateMotion(); }, { signal });
      this.reduced.addEventListener('change', () => { this.enabled=!this.reduced.matches; this.updateMotion(); }, { signal });
      document.addEventListener('visibilitychange', () => {
        this.updateMotion();
        if (document.hidden) this.querySelectorAll('[data-bcp-videos] video').forEach(video => video.pause());
      }, { signal });
      window.addEventListener('resize', () => this.resize(), { signal, passive:true });
      this.observer = new IntersectionObserver(([entry]) => { this.active=entry.isIntersecting; this.updateMotion(); });
      this.observer.observe(this);
      this.resize();
    }
    setupVideos(signal) {
      const showcase = this.querySelector('[data-bcp-videos]');
      if (!showcase) return;
      const track = showcase.querySelector('[data-video-track]');
      const controls = showcase.querySelector('[data-video-controls]');
      const previous = controls.querySelector('[data-video-step="-1"]');
      const next = controls.querySelector('[data-video-step="1"]');
      const refresh = () => {
        const max = track.scrollWidth - track.clientWidth;
        controls.hidden = max < 2;
        previous.disabled = track.scrollLeft < 2;
        next.disabled = track.scrollLeft >= max - 2;
      };
      const move = direction => {
        const card = track.querySelector('[data-bcp-video-card]');
        const distance = card ? card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0) : track.clientWidth;
        track.scrollBy({ left: direction * distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      controls.addEventListener('click', event => {
        const button = event.target.closest('[data-video-step]');
        if (button) move(Number(button.dataset.videoStep));
      }, { signal });
      track.addEventListener('keydown', event => {
        if (event.target !== track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); track.scrollLeft = event.key === 'Home' ? 0 : track.scrollWidth;
        }
      }, { signal });
      track.addEventListener('scroll', refresh, { signal, passive: true });
      track.addEventListener('play', event => {
        track.querySelectorAll('video').forEach(video => { if (video !== event.target) video.pause(); });
      }, { signal, capture: true });
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-bcp-video-card]');
        if (card) card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
      }, { signal });
      this.videoObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (!entry.isIntersecting || entry.intersectionRatio < .25) entry.target.pause(); });
      }, { threshold: [.25] });
      track.querySelectorAll('video').forEach(video => this.videoObserver.observe(video));
      this.videoResize = new ResizeObserver(refresh);
      this.videoResize.observe(track);
      refresh();
    }
    updateMotion() {
      this.toggle.hidden = !this.active || !this.hasMotion || this.reduced.matches || document.hidden;
      this.toggle.textContent = this.enabled ? 'Pause motion' : 'Resume motion';
      this.toggle.setAttribute('aria-pressed', String(this.enabled));
      this.toggleAttribute('data-motion-paused', !this.enabled || !this.active || document.hidden);
      this.schedule();
    }
    resize() {
      this.width = innerWidth; this.height = innerHeight;
      const ratio = Math.min(devicePixelRatio || 1, 2);
      this.canvas.width = this.width * ratio; this.canvas.height = this.height * ratio;
      this.ctx?.setTransform(ratio,0,0,ratio,0,0);
      this.schedule();
    }
    schedule() {
      if (!this.frame) this.frame=requestAnimationFrame(time => { this.frame=0; this.draw(time); });
    }
    draw(time) {
      if (!this.ctx) return;
      this.ctx.clearRect(0,0,this.width,this.height);
      if (!this.sprinkles || !this.active || !this.enabled || this.reduced.matches || document.hidden) { this.lastTime=null; return; }
      const elapsed=this.lastTime===null ? 0 : Math.min((time-this.lastTime)/1000,.05);
      this.lastTime=time;
      this.travel += elapsed*30*(Number(this.dataset.fallSpeed)||2);
      const rect=this.getBoundingClientRect();
      const top=Math.max(0,rect.top),bottom=Math.min(this.height,rect.bottom);
      this.ctx.save(); this.ctx.beginPath(); this.ctx.rect(0,top,this.width,Math.max(0,bottom-top)); this.ctx.clip();
      this.particles.slice(0,this.width<700?13:22).forEach(p => {
        const lane=p.x<.5?p.x*.2:.90+(p.x-.5)*.2;
        const x=lane*this.width+Math.sin(this.travel*.002+p.angle)*12;
        const y=((p.y*(this.height+40)+this.travel*p.speed)%(this.height+40))-20;
        this.ctx.save(); this.ctx.translate(x,y); this.ctx.rotate(p.angle+this.travel*.002);
        this.ctx.globalAlpha=.65; this.ctx.strokeStyle=p.color; this.ctx.lineWidth=4; this.ctx.lineCap='round';
        this.ctx.beginPath(); this.ctx.moveTo(-p.size/2,0); this.ctx.lineTo(p.size/2,0); this.ctx.stroke(); this.ctx.restore();
      });
      this.ctx.restore(); this.schedule();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.frame);
      this.videoObserver?.disconnect(); this.videoResize?.disconnect();
      this.querySelectorAll('[data-bcp-videos] video').forEach(video => video.pause());
      this.canvas?.remove(); this.toggle?.remove(); this.controller=null; this.frame=0; this.lastTime=null;
    }
  }
  customElements.define('bc-pages', BCPages);
})();


